import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Send, MessageSquare, Users, User, Check, CheckCheck, Reply, CreditCard, BookOpen, Calendar, Calculator, Trash2, Video, Eye } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Alle Empfängergruppen für Nachrichten
const RECIPIENT_GROUPS = [
  { id: "all", label: "Alle Teilnehmer", icon: Users, description: "Alle registrierten Teilnehmer" },
  { id: "all_abos", label: "Alle Abonnenten", icon: CreditCard, description: "BQ-Abo + HQ-Abo" },
  { id: "all_intensiv", label: "Alle Intensiv-Teilnehmer", icon: BookOpen, description: "BQ-Intensiv + HQ-Intensiv" },
  { id: "bq_abo", label: "Nur BQ-Abos", icon: CreditCard, description: "Nur BQ-Abonnement" },
  { id: "hq_abo", label: "Nur HQ-Abos", icon: CreditCard, description: "Nur HQ-Abonnement" },
  { id: "bq_intensiv", label: "Nur BQ-Intensiv", icon: BookOpen, description: "Nur BQ-Intensivkurs" },
  { id: "hq_intensiv", label: "Nur HQ-Intensiv", icon: BookOpen, description: "Nur HQ-Intensivkurs" },
  { id: "probetraining", label: "Nur Probetraining", icon: Calendar, description: "Nur Probetraining" },
  { id: "mathe_grundkurs", label: "Nur Mathe-Grundkurs", icon: Calculator, description: "Nur Mathe-Grundkurs" },
];

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [sending, setSending] = useState(false);
  const [readStatusDialog, setReadStatusDialog] = useState({ open: false, data: null, loading: false });
  
  const [form, setForm] = useState({
    receiver_id: "",
    subject: "",
    content: "",
    teams_link: ""
  });
  
  // Mehrfachauswahl für Gruppen
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [bulkForm, setBulkForm] = useState({
    subject: "",
    content: "",
    teams_link: ""
  });
  
  const [replyForm, setReplyForm] = useState({
    content: "",
    teams_link: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [messagesRes, participantsRes] = await Promise.all([
        axios.get(`${API}/messages`),
        axios.get(`${API}/users`)
      ]);
      setMessages(messagesRes.data);
      setParticipants(participantsRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        receiver_id: form.receiver_id,
        subject: form.subject,
        content: form.content,
        teams_link: form.teams_link || null
      });
      toast.success("Nachricht gesendet");
      setDialogOpen(false);
      setForm({ receiver_id: "", subject: "", content: "", teams_link: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSending(false);
    }
  };

  const toggleGroup = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(g => g !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSendBulkMessage = async (e) => {
    e.preventDefault();
    
    if (selectedGroups.length === 0) {
      toast.error("Bitte mindestens eine Gruppe auswählen");
      return;
    }
    
    setSending(true);
    try {
      // Sende an Backend mit Gruppenauswahl - Backend dedupliziert
      const response = await axios.post(`${API}/messages/groups`, {
        groups: selectedGroups,
        subject: bulkForm.subject,
        content: bulkForm.content,
        teams_link: bulkForm.teams_link || null
      });
      
      const count = response.data.recipients_count || 0;
      toast.success(`Nachricht an ${count} Teilnehmer gesendet (ohne Duplikate)`);
      setBulkDialogOpen(false);
      setSelectedGroups([]);
      setBulkForm({ subject: "", content: "", teams_link: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSending(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        receiver_id: selectedMessage.sender_id,
        subject: `Re: ${selectedMessage.subject}`,
        content: replyForm.content,
        parent_id: selectedMessage.id,
        teams_link: replyForm.teams_link || null
      });
      toast.success("Antwort gesendet");
      setReplyDialogOpen(false);
      setReplyForm({ content: "", teams_link: "" });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Antworten");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), "dd. MMM yyyy, HH:mm", { locale: de });
  };

  const openReplyDialog = (msg) => {
    setSelectedMessage(msg);
    setReplyDialogOpen(true);
  };

  const handleDeleteMessage = async (msgId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/messages/${msgId}`);
      toast.success("Nachricht gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleDeleteAllMessages = async () => {
    try {
      const response = await axios.delete(`${API}/messages`);
      toast.success(`${response.data.count} Nachrichten gelöscht`);
      setDeleteAllDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const openReadStatus = async (bulkSendId, e) => {
    e.stopPropagation();
    setReadStatusDialog({ open: true, data: null, loading: true });
    try {
      const res = await axios.get(`${API}/messages/bulk/${bulkSendId}/read-status`);
      setReadStatusDialog({ open: true, data: res.data, loading: false });
    } catch (error) {
      toast.error("Lesebestätigungen konnten nicht geladen werden");
      setReadStatusDialog({ open: false, data: null, loading: false });
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(/\s+/).slice(0, 2).map((n) => n[0]?.toUpperCase() || "").join("");
  };

  return (
    <AdminLayout title="Nachrichtensystem">
      <div className="space-y-6" data-testid="messages-page">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div>
            {messages.length > 0 && (
              <Button 
                onClick={() => setDeleteAllDialogOpen(true)} 
                variant="outline" 
                className="gap-2 text-destructive hover:text-destructive"
                data-testid="delete-all-messages-btn"
              >
                <Trash2 className="h-4 w-4" />
                Alle löschen
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setDialogOpen(true)} variant="outline" className="gap-2" data-testid="send-single-btn">
              <User className="h-4 w-4" />
              Einzelne Nachricht
            </Button>
            <Button onClick={() => setBulkDialogOpen(true)} className="gap-2" data-testid="send-bulk-btn">
              <Users className="h-4 w-4" />
              Gruppennachricht
            </Button>
          </div>
        </div>

        {/* Messages List */}
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Alle Nachrichten ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Keine Nachrichten vorhanden</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-border">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className="p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
                      onClick={() => openReplyDialog(msg)}
                      data-testid={`message-${msg.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium">{msg.sender_name}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="font-medium">{msg.receiver_name}</span>
                            {msg.parent_id && (
                              <Badge variant="outline" className="text-xs">
                                <Reply className="h-3 w-3 mr-1" />
                                Antwort
                              </Badge>
                            )}
                            {msg.target_booking_type && (
                              <Badge className="bg-primary/15 text-primary text-xs">
                                Gruppe: {msg.target_booking_type}
                              </Badge>
                            )}
                          </div>
                          <p className="font-medium text-primary mb-1">{msg.subject}</p>
                          <p className="text-sm text-muted-foreground line-clamp-2">{msg.content}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                            {formatDate(msg.created_at)}
                          </span>
                          <div className="flex items-center gap-2">
                            {msg.bulk_send_id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => openReadStatus(msg.bulk_send_id, e)}
                                title="Lesebestätigungen anzeigen"
                                data-testid={`read-status-${msg.bulk_send_id}`}
                              >
                                <Eye className="h-3.5 w-3.5 text-primary" />
                              </Button>
                            )}
                            {msg.is_read ? (
                              <CheckCheck className="h-4 w-4 text-green-500" />
                            ) : (
                              <Check className="h-4 w-4 text-muted-foreground" />
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => handleDeleteMessage(msg.id, e)}
                              title="Löschen"
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Delete All Messages Dialog */}
        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Alle Nachrichten löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie wirklich alle {messages.length} Nachrichten löschen? Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteAllMessages}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Alle löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Single Message Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border max-h-[90vh] flex flex-col p-0 sm:max-w-[500px]" data-testid="single-message-dialog">
            <DialogHeader className="p-6 pb-3 flex-shrink-0">
              <DialogTitle>Nachricht senden</DialogTitle>
              <DialogDescription>
                Senden Sie eine Nachricht an einen einzelnen Teilnehmer.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSendMessage} className="flex flex-col flex-1 min-h-0">
              <div className="px-6 space-y-4 overflow-y-auto flex-1 pb-4">
              <div className="space-y-2">
                <Label>Empfänger</Label>
                <Select
                  value={form.receiver_id}
                  onValueChange={(value) => setForm({ ...form, receiver_id: value })}
                >
                  <SelectTrigger className="bg-background border-input" data-testid="receiver-select">
                    <SelectValue placeholder="Teilnehmer auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {participants.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Betreff</Label>
                <Input
                  id="subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Betreff"
                  required
                  className="bg-background border-input"
                  data-testid="subject-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="content">Nachricht</Label>
                  <Button
                    type="submit"
                    size="sm"
                    className="gap-2 h-8"
                    disabled={sending}
                    data-testid="send-message-inline-btn"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {sending ? "Sende..." : "Senden"}
                  </Button>
                </div>
                <Textarea
                  id="content"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Ihre Nachricht..."
                  rows={5}
                  required
                  className="bg-background border-input resize-none"
                  data-testid="content-input"
                />
              </div>
              {/* Teams Link Field for single messages */}
              <div className="space-y-2">
                <Label htmlFor="singleTeamsLink" className="flex items-center gap-2">
                  <Video className="h-4 w-4 text-blue-500" />
                  Microsoft Teams Link (optional)
                </Label>
                <Input
                  id="singleTeamsLink"
                  value={form.teams_link}
                  onChange={(e) => setForm({ ...form, teams_link: e.target.value })}
                  placeholder="https://teams.microsoft.com/l/meetup-join/..."
                  className="bg-background border-input"
                  data-testid="single-teams-link-input"
                />
                <p className="text-xs text-muted-foreground">
                  Erscheint beim Teilnehmer als „Teams beitreten"-Button.
                </p>
              </div>
              </div>
              <DialogFooter className="p-6 pt-3 border-t border-border bg-card flex-shrink-0">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="gap-2" disabled={sending} data-testid="send-message-btn">
                  <Send className="h-4 w-4" />
                  {sending ? "Sende..." : "Senden"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Message Dialog with Multiple Selection */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="bulk-message-dialog">
            <DialogHeader className="p-6 pb-3 flex-shrink-0">
              <DialogTitle>Gruppennachricht senden</DialogTitle>
              <DialogDescription>
                Wählen Sie eine oder mehrere Gruppen. Teilnehmer erhalten die Nachricht nur einmal (keine Duplikate).
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSendBulkMessage} className="flex flex-col flex-1 min-h-0">
              <div className="px-6 space-y-4 overflow-y-auto flex-1 pb-4">
              {/* Group Selection with Checkboxes */}
              <div className="space-y-2">
                <Label>Empfängergruppen (Mehrfachauswahl)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 border border-border rounded-sm bg-background">
                  {RECIPIENT_GROUPS.map((group) => {
                    const Icon = group.icon;
                    const isSelected = selectedGroups.includes(group.id);
                    
                    return (
                      <div
                        key={group.id}
                        className={`flex items-center gap-3 p-3 rounded-sm cursor-pointer transition-colors ${
                          isSelected ? "bg-primary/10 border border-primary" : "bg-secondary/30 border border-transparent hover:bg-secondary/50"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleGroup(group.id);
                        }}
                        data-testid={`group-${group.id}`}
                      >
                        <div className={`h-4 w-4 shrink-0 rounded-sm border ${isSelected ? "bg-primary border-primary" : "border-muted-foreground"} flex items-center justify-center`}>
                          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <Icon className={`h-4 w-4 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isSelected ? "text-primary" : ""}`}>{group.label}</p>
                          <p className="text-xs text-muted-foreground truncate">{group.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedGroups.length > 0 && (
                  <p className="text-sm text-primary">
                    {selectedGroups.length} Gruppe(n) ausgewählt
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bulkSubject">Betreff</Label>
                <Input
                  id="bulkSubject"
                  value={bulkForm.subject}
                  onChange={(e) => setBulkForm({ ...bulkForm, subject: e.target.value })}
                  placeholder="Betreff"
                  required
                  className="bg-background border-input"
                  data-testid="bulk-subject-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="bulkContent">Nachricht</Label>
                  <Button
                    type="submit"
                    size="sm"
                    className="gap-2 h-8"
                    disabled={sending || selectedGroups.length === 0}
                    data-testid="send-bulk-inline-btn"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {sending ? "Sende..." : `Senden${selectedGroups.length > 0 ? ` (${selectedGroups.length})` : ""}`}
                  </Button>
                </div>
                <Textarea
                  id="bulkContent"
                  value={bulkForm.content}
                  onChange={(e) => setBulkForm({ ...bulkForm, content: e.target.value })}
                  placeholder="Ihre Nachricht..."
                  rows={5}
                  required
                  className="bg-background border-input resize-none"
                  data-testid="bulk-content-input"
                />
              </div>
              
              {/* Teams Link Field */}
              <div className="space-y-2">
                <Label htmlFor="teamsLink" className="flex items-center gap-2">
                  <Video className="h-4 w-4 text-blue-500" />
                  Microsoft Teams Link (optional)
                </Label>
                <Input
                  id="teamsLink"
                  value={bulkForm.teams_link}
                  onChange={(e) => setBulkForm({ ...bulkForm, teams_link: e.target.value })}
                  placeholder="https://teams.microsoft.com/l/meetup-join/..."
                  className="bg-background border-input"
                  data-testid="teams-link-input"
                />
                <p className="text-xs text-muted-foreground">
                  Fügen Sie einen Teams-Link hinzu, um Teilnehmern direkten Zugang zum Live-Unterricht zu geben.
                </p>
              </div>
              </div>
              
              <DialogFooter className="p-6 pt-3 border-t border-border bg-card flex-shrink-0">
                <Button type="button" variant="ghost" onClick={() => setBulkDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button 
                  type="submit" 
                  className="gap-2" 
                  disabled={sending || selectedGroups.length === 0}
                  data-testid="send-bulk-message-btn"
                >
                  <Send className="h-4 w-4" />
                  {sending ? "Sende..." : `An ${selectedGroups.length} Gruppe(n) senden`}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reply Dialog */}
        <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
          <DialogContent className="bg-card border-border max-h-[90vh] flex flex-col p-0 sm:max-w-[500px]" data-testid="reply-dialog">
            <DialogHeader className="p-6 pb-3 flex-shrink-0">
              <DialogTitle>Antworten</DialogTitle>
              <DialogDescription>
                Antwort auf: {selectedMessage?.subject}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleReply} className="flex flex-col flex-1 min-h-0">
              <div className="px-6 space-y-4 overflow-y-auto flex-1 pb-4">
              <div className="p-3 bg-secondary/30 rounded-sm border border-border">
                <p className="text-sm text-muted-foreground mb-1">
                  Von: {selectedMessage?.sender_name}
                </p>
                <p className="text-sm">{selectedMessage?.content}</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="replyContent">Ihre Antwort</Label>
                  <Button
                    type="submit"
                    size="sm"
                    className="gap-2 h-8"
                    disabled={sending}
                    data-testid="send-reply-inline-btn"
                  >
                    <Reply className="h-3.5 w-3.5" />
                    {sending ? "Sende..." : "Antworten"}
                  </Button>
                </div>
                <Textarea
                  id="replyContent"
                  value={replyForm.content}
                  onChange={(e) => setReplyForm({ ...replyForm, content: e.target.value })}
                  placeholder="Ihre Antwort..."
                  rows={4}
                  required
                  className="bg-background border-input resize-none"
                  data-testid="reply-content-input"
                />
              </div>
              {/* Teams Link Field for replies */}
              <div className="space-y-2">
                <Label htmlFor="replyTeamsLink" className="flex items-center gap-2">
                  <Video className="h-4 w-4 text-blue-500" />
                  Microsoft Teams Link (optional)
                </Label>
                <Input
                  id="replyTeamsLink"
                  value={replyForm.teams_link}
                  onChange={(e) => setReplyForm({ ...replyForm, teams_link: e.target.value })}
                  placeholder="https://teams.microsoft.com/l/meetup-join/..."
                  className="bg-background border-input"
                  data-testid="reply-teams-link-input"
                />
              </div>
              </div>
              <DialogFooter className="p-6 pt-3 border-t border-border bg-card flex-shrink-0">
                <Button type="button" variant="ghost" onClick={() => setReplyDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="gap-2" disabled={sending} data-testid="send-reply-btn">
                  <Reply className="h-4 w-4" />
                  {sending ? "Sende..." : "Antworten"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        {/* Read Status Dialog with Avatar Stack */}
        <Dialog open={readStatusDialog.open} onOpenChange={(open) => setReadStatusDialog({ open, data: open ? readStatusDialog.data : null, loading: false })}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] flex flex-col p-0" data-testid="read-status-dialog">
            <DialogHeader className="p-6 pb-3 flex-shrink-0">
              <DialogTitle>Lesebestätigungen</DialogTitle>
              <DialogDescription>
                {readStatusDialog.data
                  ? `${readStatusDialog.data.read_count} von ${readStatusDialog.data.total} Empfängern haben gelesen`
                  : "Lade Daten…"}
              </DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-6 overflow-y-auto flex-1 space-y-4">
              {readStatusDialog.loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : readStatusDialog.data ? (
                <>
                  {/* Avatar Stack Summary */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-sm border border-green-500/40 bg-green-500/5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-green-500 flex items-center gap-2">
                          <CheckCheck className="h-4 w-4" />
                          Gelesen ({readStatusDialog.data.read_count})
                        </span>
                      </div>
                      <div className="flex -space-x-2 mb-3 flex-wrap">
                        {readStatusDialog.data.read_users.slice(0, 8).map((u) => (
                          <div
                            key={u.id}
                            className="w-8 h-8 rounded-full bg-green-500/20 border-2 border-card flex items-center justify-center text-[10px] font-bold text-green-500"
                            title={u.name}
                          >
                            {getInitials(u.name)}
                          </div>
                        ))}
                        {readStatusDialog.data.read_count > 8 && (
                          <div className="w-8 h-8 rounded-full bg-green-500/30 border-2 border-card flex items-center justify-center text-[10px] font-bold text-green-500">
                            +{readStatusDialog.data.read_count - 8}
                          </div>
                        )}
                      </div>
                      <ScrollArea className="h-32">
                        <div className="space-y-1">
                          {readStatusDialog.data.read_users.map((u) => (
                            <div key={u.id} className="text-xs text-muted-foreground flex justify-between gap-2">
                              <span className="truncate">{u.name}</span>
                              {u.read_at && (
                                <span className="font-mono text-[10px]">
                                  {format(new Date(u.read_at), "dd.MM. HH:mm", { locale: de })}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>

                    <div className="p-4 rounded-sm border border-yellow-500/40 bg-yellow-500/5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-yellow-500 flex items-center gap-2">
                          <Check className="h-4 w-4" />
                          Noch nicht gelesen ({readStatusDialog.data.unread_count})
                        </span>
                      </div>
                      <div className="flex -space-x-2 mb-3 flex-wrap">
                        {readStatusDialog.data.unread_users.slice(0, 8).map((u) => (
                          <div
                            key={u.id}
                            className="w-8 h-8 rounded-full bg-yellow-500/20 border-2 border-card flex items-center justify-center text-[10px] font-bold text-yellow-500"
                            title={u.name}
                          >
                            {getInitials(u.name)}
                          </div>
                        ))}
                        {readStatusDialog.data.unread_count > 8 && (
                          <div className="w-8 h-8 rounded-full bg-yellow-500/30 border-2 border-card flex items-center justify-center text-[10px] font-bold text-yellow-500">
                            +{readStatusDialog.data.unread_count - 8}
                          </div>
                        )}
                      </div>
                      <ScrollArea className="h-32">
                        <div className="space-y-1">
                          {readStatusDialog.data.unread_users.map((u) => (
                            <div key={u.id} className="text-xs text-muted-foreground truncate">
                              {u.name}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {readStatusDialog.data.total > 0 && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">
                        Lesequote: {Math.round((readStatusDialog.data.read_count / readStatusDialog.data.total) * 100)}%
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 transition-all"
                          style={{
                            width: `${(readStatusDialog.data.read_count / readStatusDialog.data.total) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </AdminLayout>
  );
};

export default Messages;
